import {
  AcademicCapIcon,
  BookOpenIcon,
  Squares2X2Icon,
  CalendarDaysIcon,
  ShoppingBagIcon,
  TrophyIcon,
  UserGroupIcon,
  BanknotesIcon,
  Cog6ToothIcon,
} from "@heroicons/react/24/outline";
import { AppStripItemConfig } from "./appStripTypes";

/**
 * App Strip configuration
 *
 * Each item can have:
 * - permissions: Direct permission check (user needs ANY one)
 * - navItemInternalNames: Shows if ANY of these nav items are visible
 * - isCertainPathOnly: Only for CertainPath companies
 *
 * For sections (Resources, Stochastic, Admin), visibility is derived from
 * whether the user can see any nav items in that section.
 *
 * For external links (Scoreboard, Training, Partner Network), we use direct
 * permissions since they don't have child nav items.
 */
export const appStripItems: AppStripItemConfig[] = [
  {
    id: "resources",
    name: "Resources",
    icon: BookOpenIcon,
    defaultRoute: "/hub",
  },
  {
    id: "stochastic",
    name: "Lead Generation",
    icon: Squares2X2Icon,
    defaultRoute: "/lead-generation/sales-and-customer-dashboard",
    // Visibility derived from stochastic nav items
    navItemInternalNames: [
      "dashboard",
      "roi_dashboard",
      "prospects",
      "customers",
      "invoices",
      "locations",
      "campaigns",
      "dashboards", // uploads parent
    ],
  },
  {
    id: "events",
    name: "Events",
    icon: CalendarDaysIcon,
    defaultRoute: "/event-registration/events",
    // Visibility derived from event-related nav items
    navItemInternalNames: ["event-directory"],
  },
  {
    id: "learning-center",
    name: "Learning Center",
    icon: AcademicCapIcon,
    defaultRoute: "/hub/learning-center/courses",
    navItemInternalNames: ["learning-center-courses"],
    // Not yet GA: visible to everyone in dev/staging, and to CertainPath
    // staff in production. In prod this flag is true → the staff-only
    // filter applies; in non-prod it's false → the gate is off for everyone.
    // Gates on the employee's own company so coaches keep it while
    // switched into client companies.
    isCertainPathEmployeeOnly:
      import.meta.env.VITE_ENVIRONMENT === "production",
  },
  {
    id: "shop",
    name: "Shop",
    icon: ShoppingBagIcon,
    defaultRoute: "/subscriptions/total-access",
    navItemInternalNames: ["total-access", "product-directory-browse"],
  },
  {
    id: "scoreboard",
    name: "Scoreboard",
    icon: TrophyIcon,
    isExternal: true,
    externalUrl: import.meta.env.VITE_SCOREBOARD_URL || "",
    // Direct permission for external link — any scoreboard permission grants access
    permissions: ["scoreboard", "scoreboard-admin", "scoreboard-coach"],
  },
  {
    id: "employee-directory",
    name: "Employee Directory",
    icon: UserGroupIcon,
    defaultRoute: "/employee-directory",
    standalone: true,
    // Live for all authenticated company users. (Was temporarily
    // CertainPath-only until the directory was ready to go live.)
  },
  {
    id: "partner-network",
    name: "Partner Network",
    icon: BanknotesIcon,
    // Direct permission — any partner-network permission grants access.
    // Non-standalone with no defaultRoute: clicking opens the PN sidebar
    // (deep links) without navigating.
    permissions: ["partner-network", "partner-network-admin"],
  },
  {
    id: "admin",
    name: "Admin",
    icon: Cog6ToothIcon,
    defaultRoute: "/admin/events",
    // Visibility derived from admin nav items (both internal admin and company admin)
    navItemInternalNames: [
      // Internal Admin items (CertainPath employees)
      "event-registration-admin",
      "email_management",
      "resource_management",
      "user_management",
      "product_catalog_admin",
      "admin", // stochastic admin section
      "sales_portal", // salespeople reach the portal through this icon
      // Company Admin items (company admins)
      "company_user_management",
      "company_settings",
    ],
  },
];
